<template>
  <section v-if="fields && items.length" class="" :style="rootStyle">
    <ContentGrid
      :desktop-default-grid="[0, 24]"
      :tablet-default-grid="[0, 16]"
      :mobile-default-grid="[0, 16]"
    >
      <div class="c-response-timeline__inner">
        <article v-for="item in items" :key="item.id" class="c-response-timeline__card">
          <AdaptiveImage
            v-if="item.fields?.backgroundImage"
            class="c-response-timeline__pattern"
            :field="item.fields.backgroundImage"
            :show="true"
            :enable-scroll-detection="false"
            :show-image-redius="false"
          />

          <div class="c-response-timeline__content">
            <AdaptiveImage
              v-if="item.fields?.icon"
              class="c-response-timeline__icon"
              :field="item.fields.icon"
              :show="true"
              :enable-scroll-detection="false"
              :show-image-redius="false"
            />

            <RichText
              v-if="item.fields?.title?.value"
              class="c-response-timeline__title"
              :field="item.fields.title"
            />

            <RichText
              v-if="item.fields?.description?.value"
              class="c-response-timeline__description"
              :field="item.fields.description"
            />
          </div>
        </article>

        <RichText
          v-if="fields.footerText?.value"
          class="c-response-timeline__footer"
          :field="fields.footerText"
        />
      </div>
    </ContentGrid>
  </section>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { RichText } from '@sitecore-jss/sitecore-jss-vue';
import type {
  ResponseTimelineItemFields,
  ResponseTimelineProps,
} from '@/types/components/ResponseTimeline';
import AdaptiveImage from '@/elements/AdaptiveImage.vue';
import utils from '@/utils';
import type { ScItem } from '@/types/sitecore';

const props = defineProps<ResponseTimelineProps>();
const fields = computed(() => props.fields);

const rootClass = computed(() => utils.overall.baseComClass(props.fields, 'c-response-timeline'));
const rootStyle = computed(() => utils.overall?.baseComStyle?.(fields.value) || {});
const normalizeImageField = (imageField: any) => {
  const src = imageField?.value?.src;
  if (
    !src ||
    (!src.startsWith('http://') && !src.startsWith('https://') && !src.startsWith('//'))
  ) {
    return imageField;
  }

  const url = new URL(src.startsWith('//') ? `https:${src}` : src);
  return {
    ...imageField,
    value: {
      ...imageField.value,
      src: `${url.pathname}${url.search}${url.hash}`,
    },
  };
};
const normalizeImageItem = (imageItem: any) => {
  if (!imageItem?.fields) return imageItem;

  return {
    ...imageItem,
    fields: {
      ...imageItem.fields,
      desktopImage: normalizeImageField(imageItem.fields.desktopImage),
      mobileImage: normalizeImageField(imageItem.fields.mobileImage),
      tabletImage: normalizeImageField(imageItem.fields.tabletImage),
    },
  };
};
const items = computed<ScItem<ResponseTimelineItemFields>[]>(() =>
  (props.fields?.items ?? []).map((item) => ({
    ...item,
    fields: item.fields
      ? {
          ...item.fields,
          icon: normalizeImageItem(item.fields.icon),
          backgroundImage: normalizeImageItem(item.fields.backgroundImage),
        }
      : item.fields,
  }))
);
</script>

<style lang="scss">
@import '../../styles/variables';
@import '../../styles/functions';
@import '../../styles/mixins';

.c-response-timeline {
  padding: 0 0 24px;

  &__inner {
    display: flex;
    flex-direction: column;
    gap: 24px;
    width: 100%;
  }

  &__card {
    position: relative;
    overflow: hidden;
    border-radius: 22px;
    background: #f3f6f4;
  }

  &__pattern {
    position: absolute;
    inset: 0;
    z-index: 0;
    width: 100%;
    height: 100%;
    pointer-events: none;
  }

  &__pattern,
  &__pattern > div,
  &__pattern .e-background-image,
  &__pattern .e-background-image__img,
  &__pattern picture,
  &__pattern img,
  &__pattern .background-image {
    width: 100%;
    height: 100%;
    overflow: hidden;
  }

  &__pattern img {
    object-fit: cover;
  }

  &__content {
    position: relative;
    z-index: 1;
    display: flex;
    min-height: inherit;
    flex-direction: column;
    justify-content: center;
    padding: 24px;
  }

  &__icon {
    width: 48px;
    height: 48px;
    margin-bottom: 10px;
  }

  &__icon img,
  &__icon picture,
  &__icon .e-background-image,
  &__icon .e-background-image__img,
  &__icon .background-image {
    display: block;
    width: 100%;
    height: 100%;
  }

  &__title {
    display: flex;
    flex-wrap: wrap;
    align-items: baseline;
    gap: 0 8px;
    margin: 0;
    color: #3d3d3d;
    font-size: 30px;
    line-height: 1.08;
    font-weight: 700;
    letter-spacing: -0.02em;
  }

  &__highlight {
    color: #6f9874;
    font-size: 54px;
    line-height: 0.9;
  }

  &__note {
    margin-left: 18px;
    font-size: 20px;
    line-height: 1.2;
  }

  &__description {
    max-width: 620px;
    margin: 18px 0 0;
    color: #3d3d3d;
    font-size: 16px;
    line-height: 1.35;
    letter-spacing: -0.01em;
  }

  &__footer {
    margin: 18px 0 0;
    color: #3d3d3d;
    font-size: 16px;
    line-height: 1.25;
    font-weight: 500;
    letter-spacing: -0.01em;
  }

  @include tablet {
    &__content {
      padding: 24px;
    }
  }

  @include tablet-landscape {
    &__inner {
      gap: 24px;
    }

    &__card {
      border-radius: 22px;
    }

    &__content {
      padding: 24px;
    }
  }
}
</style>
